import * as React from 'react'
import clx from 'classnames'

const LINE_HEIGHT = 20 
const VERTICAL_PADDING = 16 

type Props = {
  label?: string
  value?: string
  name?: string
  placeholder?: string
  error?: string | boolean
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  className?: string
  disabled?: boolean
  required?: boolean
  wrapperClassName?: string
  maxRows?: number
}


export const FieldsetInputAutosize = ({
  label,
  value,
  name,
  placeholder,
  onChange,
  error,
  className,
  disabled = false,
  required = false,
  wrapperClassName,
  maxRows = 3,
}: Props) => {
  const hasError = Boolean(error)
  const ref = React.useRef<HTMLTextAreaElement>(null)

  React.useLayoutEffect(() => {
    const el = ref.current
    if (!el) return

    const maxHeight = LINE_HEIGHT * maxRows
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden'
  }, [value, maxRows])

  return (
    <fieldset className={clx('tw-d-form-control tw-w-full', wrapperClassName)}>
      {label && (
        <label className="tw-mb-0 tw-px-1 tw-pb-0.5 tw-text-sm tw-font-medium">
          {required && <span className="tw-mr-0.5 tw-text-error">*</span>}
          {label}
        </label>
      )}
      <span
        className={clx(
          `tw-d-input tw-d-input-sm tw-d-input-primary tw-relative tw-flex tw-h-auto tw-min-h-9 tw-items-start tw-gap-2 tw-rounded-[4px] tw-border-content-base/20 !tw-px-3 tw-py-2 focus-within:!tw-border-content-base/20 focus-within:!tw-text-content-base ${disabled ? 'tw-text-content-base/30' : 'tw-text-input'}`,
          className,
          {
            '[&:not(:focus-within)]:tw-d-input-error': hasError,
            '!tw-border-base-200 !tw-bg-base-200': disabled,
          },
        )}
        style={{ maxHeight: LINE_HEIGHT * maxRows + VERTICAL_PADDING }}
      >
        <textarea
          ref={ref}
          className="tw-peer tw-w-full tw-resize-none tw-border-0 tw-bg-transparent tw-p-0 tw-leading-5 tw-outline-none"
          value={value || ''}
          name={name}
          placeholder={placeholder}
          onChange={onChange}
          disabled={disabled}
          required={required}
          rows={1}
        />
      </span>

      {hasError && typeof error === 'string' && (
        <span className="tw-text-xs tw-font-medium tw-text-error peer-focus-within:tw-opacity-0">
          {error}
        </span>
      )}
    </fieldset>
  )
}
